/**
 * Stage labels for the progress indicator during dialogue generation.
 * Each language has localized labels for the 6 stages of the generation process.
 */

import type { LocaleCode } from "@/lib/api/client";

export type StageName =
  | "generating"
  | "streaming"
  | "structuring"
  | "translating"
  | "extracting"
  | "enriching"
  | "finishing"
  | "adapting";

const stageLabels: Record<string, Record<StageName, string>> = {
  en: {
    generating: "Generating dialogue...",
    streaming: "Building conversation...",
    structuring: "Extracting structure...",
    translating: "Preparing dialogue...",
    extracting: "Extracting words...",
    enriching: "Building vocabulary...",
    finishing: "Finishing up...",
    adapting: "Preparing dialogue...",
  },
  es: {
    generating: "Generando diálogo...",
    streaming: "Construyendo conversación...",
    structuring: "Extrayendo estructura...",
    translating: "Preparando diálogo...",
    extracting: "Extrayendo palabras...",
    enriching: "Construyendo vocabulario...",
    finishing: "Finalizando...",
    adapting: "Preparando diálogo...",
  },
  pt: {
    generating: "Gerando diálogo...",
    streaming: "Construindo conversa...",
    structuring: "Extraindo estrutura...",
    translating: "Preparando diálogo...",
    extracting: "Extraindo palavras...",
    enriching: "Construindo vocabulário...",
    finishing: "Finalizando...",
    adapting: "Preparando diálogo...",
  },
  fr: {
    generating: "Génération du dialogue...",
    streaming: "Construction de la conversation...",
    structuring: "Extraction de la structure...",
    translating: "Préparation du dialogue...",
    extracting: "Extraction des mots...",
    enriching: "Construction du vocabulaire...",
    finishing: "Finalisation...",
    adapting: "Préparation du dialogue...",
  },
  de: {
    generating: "Dialog wird generiert...",
    streaming: "Gespräch wird aufgebaut...",
    structuring: "Struktur wird extrahiert...",
    translating: "Dialog wird vorbereitet...",
    extracting: "Wörter werden extrahiert...",
    enriching: "Vokabular wird aufgebaut...",
    finishing: "Fertigstellung...",
    adapting: "Dialog wird vorbereitet...",
  },
  it: {
    generating: "Generazione del dialogo...",
    streaming: "Costruzione della conversazione...",
    structuring: "Estrazione della struttura...",
    translating: "Preparazione del dialogo...",
    extracting: "Estrazione delle parole...",
    enriching: "Costruzione del vocabolario...",
    finishing: "Finalizzazione...",
    adapting: "Preparazione del dialogo...",
  },
  ru: {
    generating: "Создаём диалог...",
    streaming: "Строим разговор...",
    structuring: "Продумываем сценарий...",
    translating: "Подготавливаем диалог...",
    extracting: "Подбираем слова...",
    enriching: "Собираем словарь...",
    finishing: "Почти готово...",
    adapting: "Подготавливаем диалог...",
  },
  ja: {
    generating: "対話を生成中...",
    streaming: "会話を構築中...",
    structuring: "構造を抽出中...",
    translating: "対話を準備中...",
    extracting: "単語を抽出中...",
    enriching: "語彙を構築中...",
    finishing: "仕上げ中...",
    adapting: "対話を準備中...",
  },
  ko: {
    generating: "대화 생성 중...",
    streaming: "대화 구성 중...",
    structuring: "구조 추출 중...",
    translating: "대화 준비 중...",
    extracting: "단어 추출 중...",
    enriching: "어휘 구축 중...",
    finishing: "마무리 중...",
    adapting: "대화 준비 중...",
  },
  "zh-CN": {
    generating: "生成对话中...",
    streaming: "构建对话中...",
    structuring: "提取结构中...",
    translating: "准备对话中...",
    extracting: "提取词汇中...",
    enriching: "构建词汇表中...",
    finishing: "即将完成...",
    adapting: "准备对话中...",
  },
  he: {
    generating: "יוצרים דיאלוג...",
    streaming: "בונים שיחה...",
    structuring: "בונים תרחיש...",
    translating: "מכינים את הדיאלוג...",
    extracting: "מחפשים מילים...",
    enriching: "בונים אוצר מילים...",
    finishing: "כמעט מוכן...",
    adapting: "מכינים את הדיאלוג...",
  },
  pl: {
    generating: "Tworzymy dialog...",
    streaming: "Budujemy rozmowę...",
    structuring: "Budujemy scenariusz...",
    translating: "Przygotowujemy dialog...",
    extracting: "Dobieramy słowa...",
    enriching: "Budujemy słownik...",
    finishing: "Prawie gotowe...",
    adapting: "Przygotowujemy dialog...",
  },
  uk: {
    generating: "Створюємо діалог...",
    streaming: "Будуємо розмову...",
    structuring: "Продумуємо сценарій...",
    translating: "Підготовлюємо діалог...",
    extracting: "Добираємо слова...",
    enriching: "Збираємо словник...",
    finishing: "Майже готово...",
    adapting: "Підготовлюємо діалог...",
  },
  ar: {
    generating: "إنشاء الحوار...",
    streaming: "بناء المحادثة...",
    structuring: "بناء السيناريو...",
    translating: "تحضير الحوار...",
    extracting: "اختيار الكلمات...",
    enriching: "بناء المفردات...",
    finishing: "على وشك الانتهاء...",
    adapting: "تحضير الحوار...",
  },
  nl: {
    generating: "Dialoog maken...",
    streaming: "Gesprek opbouwen...",
    structuring: "Scenario opbouwen...",
    translating: "Dialoog voorbereiden...",
    extracting: "Woorden selecteren...",
    enriching: "Woordenlijst opbouwen...",
    finishing: "Bijna klaar...",
    adapting: "Dialoog voorbereiden...",
  },
};

/**
 * Get the localized label for a generation stage.
 * Falls back to English if the language is not found.
 *
 * @param locale - The locale code (e.g., "en", "pt-PT", "zh-CN")
 * @param stage - The stage name
 * @returns The localized stage label
 */
export function getStageLabel(locale: LocaleCode, stage: StageName): string {
  // Extract base language from locale (e.g., "pt-PT" -> "pt")
  // Special case for zh-CN which should stay as-is
  const baseLanguage = locale === "zh-CN" ? "zh-CN" : locale.split("-")[0]!;

  // Get labels for the language, falling back to English
  const labels = stageLabels[baseLanguage] || stageLabels["en"]!;

  return labels[stage];
}
